import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Settings, APP_CONFIG } from '../config/settings';
import { combine, appendQuery } from '../shared/utilities/route';
import { PagingToken, FetchArgs, PagingTokenFixed, emptyPagingToken } from '../shared/models/core';
import { WorkingListGridItem } from './working-list/models/working-list-grid-item.model';
import { PrestartGridLookups } from 'src/app/credentialing/prestart/grid/models/prestart-grid-lookups';
import { AutoAssignType } from 'src/app/credentialing/working-list/models/auto-assign-type.model';
import { AutoAssignSettingChange } from 'src/app/credentialing/working-list/models/auto-assign-setting-change.model';
import { NotificationType } from 'src/app/credentialing/working-list/models/notification-type.model';
import { NotificationSettingChange } from 'src/app/credentialing/working-list/models/notification-setting-change.model';
import { CredentialingPermissions } from './models/credentialing-permissions.model';

export interface ValidationResult {
    name: string;
    isValid?: boolean;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CredentialingService {
    private readonly coreApiBasePath: string;
    private readonly coreApiPath: string;
    private readonly notificationApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiBasePath = combine(settings.coreAPIUrl, 'ayanova');
        this.coreApiPath = combine(this.coreApiBasePath, 'credentialing');
        this.notificationApiPath = combine(this.coreApiBasePath, 'notification');
    }

    protected getPaged<T>(pageSize: number, skip: number, url: string): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<T[]>>(path, { observe: 'response' }).pipe(
            map((resp) => {
                return resp.body;
            })
        );
    }

    getGridLookups(): Observable<PrestartGridLookups> {
        const url = `${this.coreApiPath}/lookups`;
        return this.http.get<PrestartGridLookups>(url);
    }

    protected getPagedNew<T>(url: string, args: FetchArgs): Observable<PagingTokenFixed<T[]>> {
        return this.http.post<PagingTokenFixed<T[]>>(url, args).pipe(
            map((resp) => {
                return resp ?? emptyPagingToken<T>();
            }),
            catchError((error) => {
                console.log(error);
                return of(emptyPagingToken<T>());
            })
        );
    }

    searchWorkingList(args: FetchArgs): Observable<any> {
        const url = `${this.coreApiPath}/WorkingListSearch`;
        return this.getPagedNew<WorkingListGridItem>(url, args);
    }

    /**
     * Given the searchText, returns a list of results.
     * @param searchText
     */
    lookupFacilities(searchText: string): Observable<string[]> {
        const facilitiesFilter = 'lookup/filter-facilities?searchString=';
        const url = combine(this.coreApiPath, facilitiesFilter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    /**
     * Given the searchText, returns a list of results.
     * @param searchText
     */
    lookupRecruiters(searchText: string): Observable<string[]> {
        const facilitiesFilter = 'lookup/filter-recruiters?searchString=';
        const url = combine(this.coreApiPath, facilitiesFilter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    /**
     * Given the searchText, returns a list of results.
     * @param searchText
     */
    lookupUser(searchText: string): Observable<string[]> {
        const userFilter = 'lookup/filter-users?searchString=';
        const url = combine(this.coreApiPath, userFilter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    lookupHosptialSystems(searchText: string): Observable<string[]> {
        const hospitalSystemsFilter = 'lookup/filter-systems?searchString=';
        const url = combine(this.coreApiPath, hospitalSystemsFilter + encodeURIComponent(searchText));
        return this.http.get<string[]>(url);
    }

    /**
     * Given the searchText, returns a list of results.
     * @param searchText
     */
    lookupCompliance(searchText: string): Observable<string[]> {
        const userFilter = 'lookup/filter-passport-users?searchString=';
        const url = combine(this.coreApiPath, userFilter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    getAssignmentSwitch(autoAssignId: number): Observable<AutoAssignType> {
        return this.http.get<AutoAssignType>(
            `${this.coreApiPath}/AutoAssign/GetSwitch?autoAssignTypeId=${autoAssignId}`
        );
    }

    updateAssignmentSwitch(params: AutoAssignSettingChange): Observable<unknown> {
        return this.http.post<unknown>(`${this.coreApiPath}/AutoAssign/UpdateSwitch`, params);
    }

    getNotificationSetting(notificationId: number): Observable<NotificationType> {
        return this.http.get<NotificationType>(
            `${this.notificationApiPath}/Get?notificationCategoryId=${notificationId}`
        );
    }

    updateNotificationSetting(params: NotificationSettingChange): Observable<unknown> {
        return this.http.post<unknown>(`${this.notificationApiPath}/UpdateNotificationSwitch`, params);
    }

    getCredentialingPermissions(): Observable<CredentialingPermissions> {
        return this.http.get<CredentialingPermissions>(`${this.coreApiPath}/permissions`);
    }
}
